import { ForwardedRef, forwardRef, ReactElement, ReactNode, useMemo, useRef } from 'react';

import { Box, rem, SystemStyleObject } from '@vimeo/bokeh';
import { ArrowDown, ArrowUp, ReviewQuestion } from '@vimeo/bokeh-icons';
import { IBaseDomElementProps } from '@vimeo/core/types/dom';
import { Optional } from '@vimeo/core/types/generic';
import { useScrollbarStyles } from '@vimeo/live-core/hooks/styling/useScrollbarStyles';
import { EQnaTab, IQnAQuestion } from '@vimeo/live-core/types/interaction_tools';

import { createInteractionDomName } from '../../../../libs/shared/utils/dom/identifying';
import { translations } from '../../../../translations';
import { EmptyStatePlaceholder } from '../../../shared/tools/shared';
import { createTabListColumnStyle, createTabListScrollStyle } from '../../../shared/tools/styles';
import { QuestionListNotification } from './QuestionListNotification';
import { QuestionsListSorting } from './QuestionsListSorting';
import { useQuestionListNotification } from './useQuestionListNotification';
import { useQuestionsListScrollOnPinnedQuestionChange } from './useQuestionsListScrollOnPinnedQuestionChange';
import { useQuestionsListSorting } from './useQuestionsListSorting';

interface IQuestionsListProps extends IBaseDomElementProps {
  tabId: EQnaTab;
  selectedQuestionElement?: Optional<HTMLDivElement>;
  questions: Array<IQnAQuestion>;
  pinnedQuestionId?: Optional<string>;
  itemRenderer: (question: IQnAQuestion) => ReactNode;
  fullWidth?: boolean;
  isWithSorting?: boolean;
  isManagementAccessed?: boolean;
  placeholder?: ReactElement;
  scrollBackground?: string | undefined;
  onSortingChange?: () => void;
}

// todo: Does it need questions list at all? Probably just isEmpty flag.
export const QuestionsList = forwardRef(
  (
    {
      id = createInteractionDomName('qna-list'),
      className = createInteractionDomName('qna-list'),
      tabId,
      selectedQuestionElement = null,
      questions = [],
      pinnedQuestionId,
      fullWidth,
      isManagementAccessed = false,
      itemRenderer,
      placeholder,
      scrollBackground = 'surface',
    }: IQuestionsListProps,
    ref: ForwardedRef<HTMLDivElement>,
  ) => {
    const listRef = useRef<Optional<HTMLDivElement>>(null);
    const [listSorting, changeListSorting, sortingFunction] = useQuestionsListSorting(tabId);

    const {
      canShowNewQuestionsNotification,
      canShowScrolledAway,
      scrolledAwayNotificationPosition,
      newQuestionNotificationPosition,
      onScrollInQuestionList,
      onConfirmShowNewQuestions,
      onConfirmScrollToSelectedQuestion,
      onDismissNewQuestionsNotification,
    } = useQuestionListNotification(listRef, questions, listSorting, selectedQuestionElement);

    useQuestionsListScrollOnPinnedQuestionChange(listRef, pinnedQuestionId, isManagementAccessed);

    const { pinnedQuestion, sortedQuestions } = useMemo(() => {
      const sortedQuestions = questions.sort(sortingFunction);

      if (pinnedQuestionId) {
        return {
          pinnedQuestion: sortedQuestions.find((it) => it.id === pinnedQuestionId),
          sortedQuestions: sortedQuestions.filter((it) => it.id !== pinnedQuestionId),
        };
      } else {
        return { pinnedQuestion: null, sortedQuestions };
      }
    }, [pinnedQuestionId, questions, sortingFunction]);

    const scrollbarStyles: SystemStyleObject = useScrollbarStyles({
      scrollbarColor: scrollBackground,
    });

    if (questions.length) {
      return (
        <>
          <QuestionsListSorting
            tabId={tabId}
            sorting={listSorting}
            changeSorting={changeListSorting}
          />

          <Box
            ref={ref}
            id={createInteractionDomName(id, 'scroll')}
            className={createInteractionDomName(id, 'scroll')}
            sx={{
              ...createTabListScrollStyle({ fullWidth }),
              ...scrollbarStyles,
              scrollPaddingBottom: rem(16),
            }}
            onScroll={onScrollInQuestionList}
          >
            <Box
              ref={listRef}
              id={createInteractionDomName(id, 'columns')}
              className={createInteractionDomName(className, 'columns')}
              sx={createTabListColumnStyle()}
            >
              {pinnedQuestion ? itemRenderer(pinnedQuestion) : null}
              {sortedQuestions.map((it) => itemRenderer(it))}
            </Box>
          </Box>

          {canShowNewQuestionsNotification ? (
            <QuestionListNotification
              id={createInteractionDomName(id, 'new-questions-notification')}
              className={createInteractionDomName(className, 'new-questions-notification')}
              placement={newQuestionNotificationPosition}
              content={translations.unreadQuestions}
              onDismiss={onDismissNewQuestionsNotification}
              onClick={onConfirmShowNewQuestions}
            />
          ) : null}

          {canShowScrolledAway ? (
            <QuestionListNotification
              id={createInteractionDomName(id, 'scrolled-away-notification')}
              className={createInteractionDomName(className, 'scrolled-away-notification')}
              placement={scrolledAwayNotificationPosition as string}
              content={translations.selected}
              onClick={onConfirmScrollToSelectedQuestion}
              icon={scrolledAwayNotificationPosition === 'top' ? <ArrowUp /> : <ArrowDown />}
            />
          ) : null}
        </>
      );
    } else {
      return (
        placeholder || (
          <EmptyStatePlaceholder
            id={createInteractionDomName(id, 'placeholder')}
            className={createInteractionDomName(className, 'placeholder')}
            isWithPadding={true}
            icon={<ReviewQuestion boxSize={'lg'} />}
            description={translations.noQuestionsYet}
            control={null}
          />
        )
      );
    }
  },
);
